import { Component, computed, OnDestroy, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { NbTabComponent } from '@nebular/theme';
import { ProtocolTypeService } from '@artemis-software/wr-api';
import { ProtocolTypeFormComponent } from './protocol-type-form/protocol-type-form.component';
import { ProtocolTypeSettingsComponent } from './protocol-type-settings/protocol-type-settings.component';
import { ProtocolTypeCollectionsComponent } from './protocol-type-collections/protocol-type-collections.component';

@Component({
  standalone: false,
  selector: 'wr-protocol-type-detail-page',
  templateUrl: './protocol-type-detail-page.component.html',
  styleUrls: ['./protocol-type-detail-page.component.scss'],
})
export class ProtocolTypeDetailPageComponent implements OnDestroy {

  private readonly destroy$ = new Subject<void>();

  readonly id = signal<string | null>(null);
  readonly canEdit = signal(false);
  readonly isNew = computed(() => this.id() === 'new');
  readonly isEditing = signal(false);

  private activeTabTitle = 'Stammdaten';

  @ViewChild(ProtocolTypeFormComponent) formComponent?: ProtocolTypeFormComponent;
  @ViewChild(ProtocolTypeSettingsComponent) settingsComponent?: ProtocolTypeSettingsComponent;
  @ViewChild(ProtocolTypeCollectionsComponent) collectionsComponent?: ProtocolTypeCollectionsComponent;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly protocolTypeService: ProtocolTypeService,
  ) {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const id = params.get('id');
      this.id.set(id);
      if (id === 'new') {
        this.canEdit.set(true);
        this.isEditing.set(true);
      } else if (id) {
        this.protocolTypeService.findById(id).subscribe(dto => {
          this.canEdit.set(dto.permissions.canEdit);
        });
      }
    });
  }

  onTabChange(tab: NbTabComponent): void {
    this.activeTabTitle = tab.tabTitle ?? 'Stammdaten';
  }

  onEdit(): void {
    this.isEditing.set(true);
    this.formComponent?.onEdit();
    this.settingsComponent?.onEdit();
    this.collectionsComponent?.onEdit();
  }

  onDelete(): void {
    this.formComponent?.onDelete();
  }

  async submit(): Promise<void> {
    let saved = false;
    switch (this.activeTabTitle) {
      case 'Stammdaten': saved = (await this.formComponent?.submit()) ?? false; break;
      case 'Einstellungen': saved = (await this.settingsComponent?.submit()) ?? false; break;
      case 'Feldgruppen': saved = (await this.collectionsComponent?.submit()) ?? false; break;
    }
    // Only leave edit mode when the save actually succeeded — otherwise the
    // footer buttons (disabled by !isEditing()) would get stuck on a validation
    // error and the user could neither retry nor cancel.
    if (saved) {
      this.isEditing.set(false);
    }
  }

  async cancel(): Promise<void> {
    await this.formComponent?.cancel();
    await this.settingsComponent?.cancel();
    await this.collectionsComponent?.cancel();
    this.isEditing.set(false);
  }

  navigateBack(): void {
    this.router.navigate(['protocolTypes']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
